import type {DoppeDtoBlogPost} from '../../client-server-common';
import {
    BymoPageActionMediaLayout,
    BymoPageActionMediaSize,
    BymoPageActionMediaSizing,
    BymoPageActionMediaViewLayout,
    DoppeActionListItemClickBehaviour,
    doppeDtoBlogPostGetMedia,
    DoppeExternalBlogPostProvider,
    doppeListActionListItemSettingsCreateDefault,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings
} from '../../client-server-common';
import {
    dateExtractLocalDateAsUtcMidnightIsoString,
    dateGetCurrentDateTime,
    HtmlObjectHorizontalPosition,
    HtmlObjectVerticalPosition,
    hideableValueGetValue,
    hideableValueIsHidden,
    overridableValueCreateOriginal } from '@wix/devzai-utils-common';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import type {
    DoppeActionTypeArticlesList
} from '../../doppe-action-types/doppe-action-type-articles-list/doppe-action-type-articles-list';
import type {
    DoppeActionTypeBlogPost
} from '../../doppe-action-types/doppe-action-type-blog-post/doppe-action-type-blog-post';

export function doppeBlogPostActionGetMediaViewDefaultSettings(
    overrides: Partial<DoppeActionMediaViewSettings> = {}
) {
    return {
        mainMediaViewSettings: {
            mediaLayout: BymoPageActionMediaLayout.Cropped,
            mediaSizing: BymoPageActionMediaSizing.Custom,
            size: BymoPageActionMediaSize.Ratio3By2,
            alignment: {
                horizontal: HtmlObjectHorizontalPosition.Center,
                vertical: HtmlObjectVerticalPosition.Center
            },
            ...overrides.mainMediaViewSettings,
        },
        mainMediaViewLayout: overrides.mainMediaViewLayout ?? BymoPageActionMediaViewLayout.WithMargins,
    } satisfies DoppeActionMediaViewSettings;
}

export function doppeBlogPostActionResolveSettingsFromDoppeDtoBlogPost(blogPost: DoppeDtoBlogPost, settings?: DoppeActionTypeBlogPost.ArticleSettings, blogPostProvider?: DoppeExternalBlogPostProvider): DoppeActionTypeBlogPost.ArticleSettings {

    return {
        showBlogWebsite: true,
        ...settings,
        blogPostImages: overridableValueCreateOriginal(doppeDtoBlogPostGetMedia(blogPost)),
        blogPostLink: blogPost.blogPostPageUrl ?? '',
        blogPostTitle: overridableValueCreateOriginal(blogPost?.title ?? ''),
        description: overridableValueCreateOriginal(blogPost.excerpt),
        blogPostProvider: blogPostProvider ?? DoppeExternalBlogPostProvider.Wix,
        blogPostProviderId: blogPost.id,
        blogPostDate: overridableValueCreateOriginal(dateExtractLocalDateAsUtcMidnightIsoString(blogPost.firstPublishedDate ?? dateGetCurrentDateTime())),
        ...doppeBlogPostActionGetMediaViewDefaultSettings()
    } satisfies DoppeActionTypeBlogPost.ArticleSettings
}

export function doppeBlogPostsListActionResolveItemSettingsFromDoppeDtoBlogPost(
    blogPost: DoppeDtoBlogPost,
    settings?: DoppeActionTypeBlogPost.ArticleSettings,
    blogPostProvider?: DoppeExternalBlogPostProvider
): DoppeActionTypeArticlesList.ArticleSettings {

    const blogPostSettings = doppeBlogPostActionResolveSettingsFromDoppeDtoBlogPost(blogPost, settings, blogPostProvider);

    return {
        ...blogPostSettings,
        blogPostTitle: hideableValueIsHidden(blogPostSettings.blogPostTitle) ?
            overridableValueCreateOriginal('') :
            hideableValueGetValue(blogPostSettings.blogPostTitle),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionListItemSettingsCreateDefault(blogPost.id),
    }

}

export function doppeBlogPostsListActionResolveExternalItemSettingsFromDoppeDtoBlogPost(
    options: {
        blogPost: DoppeDtoBlogPost;
        listItemClickBehaviour: DoppeActionListItemClickBehaviour;
        blogPostProvider?: DoppeExternalBlogPostProvider;
    }
): DoppeActionTypeArticlesList.ArticleSettings {

    const {
        blogPost,
        listItemClickBehaviour,
        blogPostProvider,
    } = options;

    const blogPostSettings = doppeBlogPostsListActionResolveItemSettingsFromDoppeDtoBlogPost(blogPost, undefined, blogPostProvider);

    return {
        ...blogPostSettings,
        mainMediaViewSettings: {
            mediaLayout: BymoPageActionMediaLayout.Cropped,
            mediaSizing: BymoPageActionMediaSizing.ShowFullImageWithHeightLimit,
            size: BymoPageActionMediaSize.Ratio3By2,
            alignment: {
                horizontal: HtmlObjectHorizontalPosition.Center,
                vertical: HtmlObjectVerticalPosition.Center
            }
        },
        mainMediaViewLayout: listItemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItemAsPopup ?
            BymoPageActionMediaViewLayout.FullWidth :
            BymoPageActionMediaViewLayout.WithMargins
    }

}